import { faFolderPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import { DocumentTitle } from 'components/DocumentTitle';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { TablePageLayout } from 'components/page/styled';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { FileDrop } from 'components/upload/FileDrop';
import { MultiFileUpload } from 'components/upload/MultiFileUpload';
import { usePermissions } from 'contexts/PermissionsContext';
import { usePagedDirectoryDataSource } from 'custom_hooks/data_hooks/data_source/directoryDataSource';
import { FileBreadcrumb } from 'pages/Settings/FileLibrary/FileBreadcrumb';
import { FolderModal } from 'pages/Settings/FileLibrary/Folder/FolderModal';
import { FileLibraryTable } from 'pages/Settings/FileLibrary/table/FileLibraryTable';
import { SearchCreateButtonContainer } from 'pages/Settings/shared/styled';
import { useEffect, useState } from 'react';
import { lastItem } from 'utils/array';

import { PathToIdMapping } from './hooks/useFolderIdsInPath';
import { FileLibraryProps } from './types';

type FileLibraryListViewProps = FileLibraryProps & {
    dataSource: ReturnType<typeof usePagedDirectoryDataSource>;
    path: PathToIdMapping[];
    resetPaging: () => void;
};

export function FileLibraryListView({
    corporation,
    location,
    dataSource,
    path,
    resetPaging,
}: FileLibraryListViewProps) {
    const { canUploadToFileLibrary } = usePermissions();
    const [nameSearch, setNameSearch] = useState('');
    const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);

    const corporationId = corporation?.Id;
    const locationId = location?.Id;
    const currentFolder = lastItem(path);

    const { setFilter } = dataSource;
    useEffect(() => {
        setFilter((cur) => ({ ...cur, nameSearch }));
    }, [nameSearch, setFilter]);

    return (
        <TablePageLayout>
            <DocumentTitle title={`${corporation?.Name ?? ''} File Library`} />
            <SearchCreateButtonContainer>
                <DebouncedSearchInput
                    placeholder="Search"
                    value={nameSearch}
                    onChange={setNameSearch}
                />
                {canUploadToFileLibrary && (
                    <div className="flex align-center">
                        <Button
                            className="mr-10"
                            title="Add Folder"
                            appearance="primary"
                            spacing="medium"
                            icon={<FontAwesomeIcon icon={faFolderPlus} />}
                            onClick={() => setAddFolderModalOpen(true)}
                        >
                            Add Folder
                        </Button>
                        <MultiFileUpload
                            locationId={location?.Id}
                            corporationId={corporation?.Id}
                            parentFolderId={currentFolder?.id}
                            onUpload={() => dataSource.reloadPage()}
                        />
                    </div>
                )}
            </SearchCreateButtonContainer>
            <FileDrop
                corporationId={corporation?.Id}
                locationId={location?.Id}
                parentFolderId={currentFolder?.id}
                folderName={currentFolder?.name}
                onUpload={() => dataSource.reloadPage()}
                disabled={!canUploadToFileLibrary}
            >
                <SpinnerArea hideChildren noContainer>
                    <FileBreadcrumb paths={path} resetPaging={resetPaging} />
                    <FileLibraryTable
                        locationId={locationId}
                        corporationId={corporationId}
                        dataSource={dataSource}
                        foldersHierarchy={path}
                    />
                    {addFolderModalOpen && (
                        <FolderModal
                            mode="create"
                            locationId={locationId}
                            corporationId={corporationId}
                            parentFolderId={currentFolder?.id}
                            onCancel={() => setAddFolderModalOpen(false)}
                            onSave={() => {
                                dataSource.reloadPage();
                                setAddFolderModalOpen(false);
                            }}
                        />
                    )}
                </SpinnerArea>
            </FileDrop>
        </TablePageLayout>
    );
}
